/* SUMMARY
  * Imports
  * ExportFieldPicker: choose which columns land in the workbook
*/

/* Imports */
import { Button, Checkbox, Typography } from "@material-tailwind/react";
import { t } from "i18next";
import { columnKey } from "../../services/export/exportFieldSelection";
/***/

/**
* ExportFieldPicker: liste des colonnes du fichier, à cocher.
*
* Une grille à plat, dans l'ordre du descripteur, et non un regroupement par thème : les sept
* descripteurs n'ont aucune métadonnée de groupe, et en inventer une pour 73 colonnes rendrait la
* liste dépendante d'un classement à maintenir. L'ordre du descripteur est déjà celui du fichier
* produit, donc ce que l'utilisateur coche se lit dans l'ordre où il le retrouvera.
*
* La zone défile au-delà d'une dizaine de lignes : les interventions en comptent 29, et une modale
* qui s'étire sur toute la hauteur de l'écran repousserait le bouton de téléchargement hors de vue.
*
* @param descriptor - toutes les colonnes disponibles
* @param selected - clés des colonnes cochées
* @param onChange - nouvelle sélection
* @param disabled - désactive la liste pendant le téléchargement
*/
export default function ExportFieldPicker({
  descriptor,
  selected,
  onChange,
  disabled
}: {
  descriptor: EasyTech.ExportDescriptor[];
  selected: string[];
  onChange: (selected: string[]) => void;
  disabled?: boolean;
}) {
  const selectedSet = new Set(selected);

  /** L'en-tête tel qu'il apparaîtra dans le fichier, pour que la case et la colonne se répondent. */
  const headerOf = (entry: EasyTech.ExportDescriptor): string =>
    entry.columnTranslationKey ? t(entry.columnTranslationKey) : entry.column;

  const toggle = (key: string) => {
    // Reconstruit depuis le descripteur plutôt que d'ajouter en fin de liste : la sélection reste
    // dans l'ordre du fichier, ce qui rend les comparaisons et les tests lisibles.
    const next = descriptor
      .map(columnKey)
      .filter((candidate) =>
        candidate === key ? !selectedSet.has(key) : selectedSet.has(candidate)
      );
    onChange(next);
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <Typography variant="small" className="font-medium text-blue-gray-700">
          {t("common:export.fields.title")}
        </Typography>

        <div className="flex gap-1">
          <Button variant="text" size="sm" className="py-1 px-2"
            disabled={disabled || selected.length === descriptor.length}
            onClick={() => onChange(descriptor.map(columnKey))}>
            {t("common:export.fields.selectAll")}
          </Button>
          <Button variant="text" size="sm" className="py-1 px-2"
            disabled={disabled || selected.length === 0}
            onClick={() => onChange([])}>
            {t("common:export.fields.selectNone")}
          </Button>
        </div>
      </div>

      <div className="max-h-56 overflow-y-auto rounded-lg border border-blue-gray-100">
        <div className="grid grid-cols-2 gap-x-2 p-1">
          {descriptor.map((entry) => {
            const key = columnKey(entry);
            return (
              <Checkbox
                key={key}
                id={`export-field-${key}`}
                checked={selectedSet.has(key)}
                disabled={disabled}
                onChange={() => toggle(key)}
                // L'onde de material-tailwind est un pseudo-élément absolu de 48 px centré sur une
                // case de 20 : il déborde de la zone défilante et y fait apparaître une barre pour
                // six pixels, même quand la liste tient entière. `ripple={false}` ne coupe que
                // l'effet JS, pas les classes `before:` — d'où la mise à zéro explicite.
                ripple={false}
                className="before:w-0 before:h-0"
                containerProps={{ className: "p-1" }}
                label={
                  <Typography variant="small" className="text-blue-gray-800">
                    {headerOf(entry)}
                  </Typography>
                }
              />
            );
          })}
        </div>
      </div>

      <Typography variant="small" className="text-blue-gray-500">
        {/* Un compteur plutôt qu'un simple message d'erreur à zéro : sur 29 colonnes repliées dans
          * une zone défilante, le nombre retenu n'est pas déductible de ce qui est à l'écran. */}
        {t("common:export.fields.count", {
          selected: selected.length,
          total: descriptor.length
        })}
      </Typography>
    </div>
  );
}
/***/
